import { useEffect, useState } from 'react'
import { supabase } from '../../supabase'
import { formatEuro, MOIS_LABELS } from '../../utils/calculs'
import { getAnneeScolaireEnCours, labelAnneeScolaire, plageAnneeScolaire } from '../../utils/anneeScolaire'

export default function Dashboard({ onNavigate }) {
  const now = new Date()
  const mois = now.getMonth() + 1
  const annee = now.getFullYear()
  const anneeScolaire = getAnneeScolaireEnCours()
  const [statsMois, setStatsMois] = useState(null)
  const [statsAnnee, setStatsAnnee] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      const debut_mois = `${annee}-${String(mois).padStart(2,'0')}-01`
      // Fix bug UTC : construction explicite sans toISOString()
      const dernierJour = new Date(annee, mois, 0).getDate()
      const fin_mois = `${annee}-${String(mois).padStart(2,'0')}-${String(dernierJour).padStart(2,'0')}`
      const { debut: debut_annee, fin: fin_annee } = plageAnneeScolaire(anneeScolaire)

      const [{ data: dm }, { data: da }] = await Promise.all([
        supabase.from('seances')
          .select('ca_ht, ca_ttc, remuneration_affichee, pole, total_massage_payant, total_massage_gratuit')
          .gte('date', debut_mois).lte('date', fin_mois),
        supabase.from('seances')
          .select('date, ca_ht, remuneration_affichee, pole')
          .gte('date', debut_annee).lte('date', fin_annee)
      ])

      if (dm) {
        setStatsMois({
          ca_ht: dm.reduce((a,s)=>a+(s.ca_ht||0),0),
          ca_ttc: dm.reduce((a,s)=>a+(s.ca_ttc||0),0),
          rem: dm.reduce((a,s)=>a+(s.remuneration_affichee||0),0),
          massages: dm.reduce((a,s)=>a+(s.total_massage_payant||0),0),
          gratuits: dm.reduce((a,s)=>a+(s.total_massage_gratuit||0),0),
          nb: dm.length,
          spa: dm.filter(s=>s.pole==='SPA').length,
          hydro: dm.filter(s=>s.pole==='HYDRO').length,
        })
      }

      // Grouper par mois pour le graphique (ordre scolaire : sept → août)
      if (da) {
        const moisScolaires = [9,10,11,12,1,2,3,4,5,6,7,8]
        const parMois = {}
        for (const m of moisScolaires) parMois[m] = { ca_ht: 0, rem: 0, seances: 0 }
        for (const s of da) {
          const m = new Date(s.date).getMonth() + 1
          parMois[m].ca_ht += s.ca_ht || 0
          parMois[m].rem += s.remuneration_affichee || 0
          parMois[m].seances += 1
        }
        setStatsAnnee(moisScolaires.map(m => ({ mois: m, ...parMois[m] })))
      }
      setLoading(false)
    }
    load()
  }, [mois, annee, anneeScolaire])

  const maxCA = Math.max(...statsAnnee.map(s => s.ca_ht), 1)
  const moisAvecDonnees = statsAnnee.filter(s => s.seances > 0)

  function labelMois(m) {
    const a = m >= 9 ? anneeScolaire : anneeScolaire + 1
    return `${MOIS_LABELS[m].slice(0,3)} ${String(a).slice(2)}`
  }

  if (loading) return <div className="spinner" />

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Dashboard</h2>
          <div className="page-header-sub">{MOIS_LABELS[mois]} {annee}</div>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="s-label">CA HT du mois</div>
          <div className="s-value">{formatEuro(statsMois?.ca_ht)}</div>
          <div className="s-sub">TTC : {formatEuro(statsMois?.ca_ttc)}</div>
        </div>
        <div className="stat-card">
          <div className="s-label">Rémunérations</div>
          <div className="s-value" style={{color:'var(--bordeaux)'}}>{formatEuro(statsMois?.rem)}</div>
          <div className="s-sub">Montants à facturer</div>
        </div>
        <div className="stat-card">
          <div className="s-label">Massages payants</div>
          <div className="s-value">{statsMois?.massages ?? 0}</div>
          <div className="s-sub">Gratuits : {statsMois?.gratuits ?? 0}</div>
        </div>
        <div className="stat-card">
          <div className="s-label">Séances saisies</div>
          <div className="s-value">{statsMois?.nb ?? 0}</div>
          <div className="s-sub">SPA : {statsMois?.spa ?? 0} · Hydro : {statsMois?.hydro ?? 0}</div>
        </div>
      </div>

      <div className="card" style={{marginBottom:20}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:20}}>
          <h3>Évolution CA HT — {labelAnneeScolaire(anneeScolaire)}</h3>
          <button className="btn btn-outline btn-sm" onClick={() => onNavigate('recap-annuel')}>
            Voir le détail →
          </button>
        </div>

        {moisAvecDonnees.length === 0 ? (
          <div style={{textAlign:'center', color:'var(--stone)', padding:'32px 0', fontSize:13}}>
            Aucune donnée pour {labelAnneeScolaire(anneeScolaire)}
          </div>
        ) : (
          <>
            <div style={{display:'flex', alignItems:'flex-end', gap:8, height:180, padding:'0 4px'}}>
              {statsAnnee.map(s => {
                const hauteur = s.ca_ht > 0 ? Math.max((s.ca_ht / maxCA) * 160, 4) : 0
                const isCurrent = s.mois === mois
                return (
                  <div key={s.mois} style={{flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4}}>
                    {s.ca_ht > 0 && (
                      <div style={{fontSize:10, color:'var(--stone)', whiteSpace:'nowrap'}}>
                        {formatEuro(s.ca_ht)}
                      </div>
                    )}
                    <div style={{
                      width:'100%', height: hauteur || 2,
                      background: isCurrent ? 'var(--bordeaux)' : s.ca_ht > 0 ? 'var(--charcoal)' : 'var(--border)',
                      borderRadius:'3px 3px 0 0', transition:'height 0.3s ease'
                    }} />
                    <div style={{fontSize:9, color: isCurrent ? 'var(--bordeaux)' : 'var(--stone)', fontWeight: isCurrent ? 600 : 400}}>
                      {labelMois(s.mois)}
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>

      <div className="card">
        <h3 style={{marginBottom:16}}>Récapitulatif mensuel — {labelAnneeScolaire(anneeScolaire)}</h3>
        <table>
          <thead>
            <tr>
              <th>Mois</th>
              <th className="num">CA HT</th>
              <th className="num">Séances</th>
              <th className="num">Rém. facturée</th>
            </tr>
          </thead>
          <tbody>
            {moisAvecDonnees.map(s => (
              <tr key={s.mois} style={s.mois === mois ? {background:'var(--cream)'} : {}}>
                <td>
                  {MOIS_LABELS[s.mois]} {s.mois >= 9 ? anneeScolaire : anneeScolaire + 1}
                  {s.mois === mois && <span className="badge badge-spa" style={{marginLeft:8}}>En cours</span>}
                </td>
                <td className="num">{formatEuro(s.ca_ht)}</td>
                <td className="num">{s.seances}</td>
                <td className="num" style={{color:'var(--bordeaux)'}}>{formatEuro(s.rem)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td><strong>Total {labelAnneeScolaire(anneeScolaire)}</strong></td>
              <td className="num"><strong>{formatEuro(statsAnnee.reduce((a,s)=>a+s.ca_ht,0))}</strong></td>
              <td className="num"><strong>{statsAnnee.reduce((a,s)=>a+s.seances,0)}</strong></td>
              <td className="num" style={{color:'var(--bordeaux)'}}><strong>{formatEuro(statsAnnee.reduce((a,s)=>a+s.rem,0))}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {statsMois?.nb === 0 && (
        <div className="alert alert-info" style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:16}}>
          <span>Aucune séance saisie pour {MOIS_LABELS[mois]} {annee}.</span>
          <button className="btn btn-primary btn-sm" onClick={() => onNavigate('saisie')}>Commencer la saisie</button>
        </div>
      )}
    </div>
  )
}
